/**
 * Structured error handling for booking-related operations.
 *
 * BookingError carries a code + context so failures (DB, email, Stripe,
 * validation, RLS denial) can be logged consistently and surfaced to the
 * user without ever failing the parent operation (e.g. a booking should
 * still succeed even if its confirmation email fails).
 */

import { Sentry } from "@/lib/sentry";
import { supabase } from "@/lib/supabase";

export type ErrorCode = "DB_QUERY" | "RLS" | "VALIDATION" | "STRIPE" | "EMAIL";

export class BookingError extends Error {
  constructor(
    public code: ErrorCode,
    public context: Record<string, unknown>,
    message: string
  ) {
    super(message);
    this.name = "BookingError";
  }
}

/**
 * Wraps a Supabase query, throwing a BookingError (instead of swallowing
 * or losing context) on either a query error or unexpectedly-null data.
 * `context.code` lets callers tag non-DB failures (e.g. "STRIPE") correctly
 * instead of everything defaulting to "DB_QUERY".
 */
export async function safeQuery<T>(
  promise: PromiseLike<{ data: T | null; error: { message: string } | null }>,
  context: { operation: string; code?: ErrorCode; [key: string]: unknown }
): Promise<T> {
  const code = context.code ?? "DB_QUERY";
  try {
    const { data, error } = await promise;
    if (error) {
      throw new BookingError(code, context, error.message);
    }
    if (data === null) {
      throw new BookingError(code, context, "No data returned from query");
    }
    return data;
  } catch (err) {
    const bookingError = err instanceof BookingError ? err : new BookingError(code, context, String(err));
    reportError(bookingError);
    throw bookingError;
  }
}

/**
 * Reports a BookingError to Sentry and persists it server-side via the
 * log-error edge function. Fire-and-forget — never throws, so callers can
 * call this from a .catch() without creating a new unhandled rejection.
 */
export function reportError(error: BookingError) {
  Sentry.captureException(error, {
    tags: { code: error.code, operation: String(error.context.operation ?? "unknown") },
    extra: error.context,
  });

  supabase.functions
    .invoke("log-error", {
      body: {
        code: error.code,
        operation: error.context.operation,
        message: error.message,
        context: error.context,
        timestamp: new Date().toISOString(),
      },
    })
    .catch((e) => console.error("[errorHandler] Failed to persist error log:", e));
}

/** Maps an error to toast copy without leaking internals to the UI. */
export function errorToastFor(error: unknown): { title: string; description: string } {
  if (error instanceof BookingError) {
    return { title: titleForCode(error.code), description: error.message };
  }
  return { title: "Something went wrong", description: "Our team has been notified." };
}

function titleForCode(code: ErrorCode): string {
  switch (code) {
    case "EMAIL":
      return "Email not sent";
    case "STRIPE":
      return "Payment error";
    case "RLS":
      return "Access denied";
    case "VALIDATION":
      return "Validation error";
    default:
      return "Something went wrong";
  }
}
